import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions, handlePermissionClick } from '../../hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Users, BookOpen, UserCheck, Copy, Clock, Ban, ShieldOff, PlayCircle, LayoutGrid, List, Search, Eye, PowerOff, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { DataToolbar } from '../../components/ui/DataToolbar';
import { DataTable, PaginationFooter } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ProfessorForm } from '../../components/forms/ProfessorForm';
import { ProfessorDetalhePage, ProfessorFichaFooter } from './ProfessorDetalhe';
import { SetupPageHero } from '../../components/onboarding/SetupPageHero';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { RowActionMenu } from '../../components/ui/RowActionMenu';
import { RegistroMeta } from '../../components/admin/registro-meta';
import { ListAvatar } from '../../components/admin/list-avatar';
import { hasPermission } from '../../auth/permissions';
import type { Professor, Materia } from '../../types/models';
import { ROUTES } from '../../api/routes';
import { normalizeActivationLink } from '../../components/invite';

export function ProfessoresPage() {
  const { canManage: canManageFn } = usePermissions();
  const canManage = canManageFn('professores');

  const { data: professores, isLoading, error: loadError, deactivate, refetch } = useCrud<Professor>('professores');

  useEffect(() => {
    if (loadError) {
      toast.error('Não foi possível carregar os professores. Tente atualizar a página.');
    }
  }, [loadError]);
  const { data: materias } = useCrud<Materia>('materias');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Professor | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'lista' | 'cards'>('cards');
  const [pageSize, setPageSize] = useState(10);
  const [fichaId, setFichaId] = useState<string | null>(null);
  const [gerando, setGerando] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Professor | null>(null);
  const [statusConfirm, setStatusConfirm] = useState<{ prof: Professor; novoStatus: string } | null>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const escolaId = user?.escolaId;
  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (p: Professor) => { setEditing(p); setFormOpen(true); };

  const requestDelete = (prof: Professor) => setDeleteConfirm(prof);
  const confirmDeleteProfessor = async () => {
    if (!deleteConfirm) return;
    const prof = deleteConfirm;
    const status = (prof as any).statusConta;
    const isPendente = status === 'PENDENTE' || status === 'ATIVANDO';
    setDeleteConfirm(null);
    try { 
      await deactivate.mutateAsync(prof.id); 
      toast.success(isPendente ? 'Convite removido' : 'Professor desativado'); 
      refetch(); 
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    } catch { 
      toast.error('Erro ao remover'); 
    }
  };

  const handleReactivate = async (p: Professor) => {
    try {
      await api.patch(ROUTES.professores.findOne(escolaId!, p.id), { ativo: true });
      toast.success(`"${p.nome}" reativado com sucesso!`);
      refetch();
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    } catch {
      toast.error('Erro ao reativar professor.');
    }
  };

  const statusLabels: Record<string, string> = { SUSPENSA: 'suspender', BLOQUEADA: 'bloquear', ATIVA: 'reativar' };
  const requestChangeStatus = (prof: Professor, novoStatus: string) => setStatusConfirm({ prof, novoStatus });
  const confirmChangeStatus = async () => {
    if (!statusConfirm || !escolaId) return;
    const { prof, novoStatus } = statusConfirm;
    setStatusConfirm(null);
    try {
      await api.patch(ROUTES.equipe.status(escolaId, prof.id), { statusConta: novoStatus });
      toast.success(`Professor ${statusLabels[novoStatus] || 'atualizado'}!`);
      refetch();
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const gerarNovoLink = async (prof: Professor) => {
    if (!escolaId) return;
    setGerando(prof.id);
    try {
      const { data } = await api.post(ROUTES.equipe.convidar(escolaId), {
        nome: prof.nome, email: prof.email, cargo: 'PROFESSOR',
        telefone: (prof as any).telefone || undefined,
      });
      if (data.activationLink) {
        navigator.clipboard.writeText(normalizeActivationLink(data.activationLink));
        toast.success('Novo link gerado e copiado!');
      }
    } catch { toast.error('Erro ao gerar link'); }
    finally { setGerando(null); }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return professores;
    return professores.filter((p) =>
      [p.nome, p.email, p.telefone, p.especialidade, ...(p.materias?.map((m) => m.nome) ?? [])]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [professores, searchQuery]);



  const ativos = (professores ?? []).filter(p => p.ativo).length;
  const getMateriasVinculadas = (prof: Professor): { id: string; nome: string }[] => {
    const fromJoin = (((prof as any).professorMaterias ?? []) as any[])
      .map((pm) => pm?.materia)
      .filter(Boolean)
      .map((m) => ({ id: String(m.id), nome: String(m.nome) }));

    const fallback = (prof.materias ?? []).map((m) => ({ id: String(m.id), nome: String(m.nome) }));
    const raw = fromJoin.length > 0 ? fromJoin : fallback;
    const unique = new Map<string, { id: string; nome: string }>();
    raw.forEach((m) => {
      if (!m.id || !m.nome) return;
      const key = m.nome.trim().toLowerCase();
      if (!unique.has(key)) unique.set(key, m);
    });
    return Array.from(unique.values());
  };

  const columns: ColumnDef<Professor>[] = [
    {
      accessorKey: 'nome',
      header: 'Professor',
      cell: ({ row }) => (
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, opacity: row.original.ativo ? 1 : 0.4 }}>
          <ListAvatar fotoUrl={(row.original as any).fotoUrl} nome={row.original.nome} role="PROFESSOR" size={44} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.original.nome}
            </div>
            <div style={{ fontSize: '.76rem', color: '#2563eb', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.original.email || '—'}
            </div>
          </div>
        </div>
      ),
    },
    { accessorKey: 'telefone', header: 'Telefone', cell: ({ row }) => <span style={{ opacity: row.original.ativo ? 1 : 0.4 }}>{row.original.telefone || '—'}</span> },
    {
      id: 'materias', header: 'Disciplinas selecionadas',
      cell: ({ row }) => {
        const m = getMateriasVinculadas(row.original);
        if (!m || m.length === 0) return <span style={{ color: '#9ca3af', opacity: row.original.ativo ? 1 : 0.4 }}>Nenhuma</span>;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', opacity: row.original.ativo ? 1 : 0.4 }}>
            {m.slice(0, 3).map(mat => (
              <span key={mat.id} style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>{mat.nome}</span>
            ))}
            {m.length > 3 && <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>+{m.length - 3}</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'ativo', header: 'Status',
      cell: ({ row }) => {
        const status = (row.original as any).statusConta;
        if (status === 'SUSPENSA') return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>
            <Ban size={12} /> Suspenso
          </span>
        );
        if (status === 'BLOQUEADA') return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, background: '#fee2e2', color: '#991b1b' }}>
            <ShieldOff size={12} /> Bloqueado
          </span>
        );
        if (status === 'PENDENTE' || status === 'ATIVANDO') return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>
            <Clock size={12} /> Pendente
          </span>
        );
        return <span className={`badge ${row.original.ativo ? 'badge-active' : 'badge-inactive'}`}>{row.original.ativo ? 'Ativo' : 'Inativo'}</span>;
      },
    },
    {
      id: 'registro',
      header: 'Registro',
      enableSorting: false,
      cell: ({ row }) => {
        const p = row.original as Professor & { criadoEm?: string; atualizadoEm?: string; vinculoCriadoEm?: string };
        const criadoEm = p.criadoEm || p.vinculoCriadoEm || '';
        return (
          <RegistroMeta
            criadoEm={criadoEm}
            atualizadoEm={p.atualizadoEm}
          />
        );
      },
    },
    {
      id: 'actions', header: '',
      cell: ({ row }) => {
        const p = row.original;
        const status = (p as any).statusConta;
        const isPendente = status === 'PENDENTE' || status === 'ATIVANDO';
        return (
          <div className="flex items-center">
            {/* Mobile: 3 pontinhos com menu de contexto */}
            <div className="flex md:hidden">
              <RowActionMenu
                items={[
                  {
                    label: 'Ver Detalhes',
                    icon: <Eye size={15} />,
                    onClick: () => setFichaId(p.id),
                    variant: 'view',
                  },
                  canManage
                    ? {
                        label: 'Editar Professor',
                        icon: <Pencil size={15} />,
                        onClick: () => openEdit(p),
                        variant: 'edit',
                      }
                    : null,
                  canManage && isPendente
                    ? {
                        label: 'Copiar Link de Ativação',
                        icon: <Copy size={15} />,
                        onClick: () => gerarNovoLink(p),
                        variant: 'primary',
                      }
                    : null,
                  canManage && status === 'ATIVA'
                    ? {
                        label: 'Suspender Professor',
                        icon: <Ban size={15} />,
                        onClick: () => requestChangeStatus(p, 'SUSPENSA'),
                        variant: 'danger',
                      }
                    : null,
                  canManage && (status === 'SUSPENSA' || status === 'BLOQUEADA')
                    ? {
                        label: 'Reativar / Desbloquear',
                        icon: <PlayCircle size={15} />,
                        onClick: () => requestChangeStatus(p, 'ATIVA'),
                        variant: 'success',
                      }
                    : null,
                  canManage && p.ativo
                    ? {
                        label: isPendente ? 'Remover Convite' : 'Desativar Professor',
                        icon: <PowerOff size={15} />,
                        onClick: () => requestDelete(p),
                        variant: 'danger',
                      }
                    : null,
                  canManage && !p.ativo
                    ? {
                        label: 'Reativar Professor',
                        icon: <RotateCcw size={15} />,
                        onClick: () => handleReactivate(p),
                        variant: 'success',
                      }
                    : null,
                ]}
              />
            </div>

            {/* Desktop: Ações diretas */}
            <div className="hidden md:flex items-center gap-1">
              {isPendente && (
                <button
                  className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title={!canManage ? 'Permissão restrita para gerar link' : 'Gerar link e copiar'}
                  onClick={() => handlePermissionClick(canManage, () => gerarNovoLink(p), 'Você não tem permissão para gerar links de ativação.')}
                  disabled={gerando === p.id}
                  style={{ color: '#2563eb' }}
                >
                  <Copy size={15} />
                </button>
              )}
              <button className="action-btn action-btn-view" title="Detalhes" onClick={() => setFichaId(p.id)}><Eye size={15} /></button>
              <button
                className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                title={!canManage ? 'Permissão restrita para edição' : 'Editar'}
                onClick={() => handlePermissionClick(canManage, () => openEdit(p), 'Você não tem permissão para editar professores.')}
              >
                <Pencil size={15} />
              </button>
              {status === 'ATIVA' && (
                <button
                  className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title={!canManage ? 'Permissão restrita para suspender' : 'Suspender'}
                  onClick={() => handlePermissionClick(canManage, () => requestChangeStatus(p, 'SUSPENSA'), 'Você não tem permissão para suspender professores.')}
                  style={{ color: '#f59e0b' }}
                >
                  <Ban size={15} />
                </button>
              )}
              {status === 'SUSPENSA' && (
                <button
                  className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title={!canManage ? 'Permissão restrita para reativar' : 'Reativar'}
                  onClick={() => handlePermissionClick(canManage, () => requestChangeStatus(p, 'ATIVA'), 'Você não tem permissão para reativar professores.')}
                  style={{ color: '#10b981' }}
                >
                  <PlayCircle size={15} />
                </button>
              )}
              {status === 'BLOQUEADA' && (
                <button
                  className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title={!canManage ? 'Permissão restrita para desbloquear' : 'Desbloquear'}
                  onClick={() => handlePermissionClick(canManage, () => requestChangeStatus(p, 'ATIVA'), 'Você não tem permissão para desbloquear professores.')}
                  style={{ color: '#10b981' }}
                >
                  <PlayCircle size={15} />
                </button>
              )}
              {p.ativo ? (
                <button
                  className={`action-btn action-btn-danger ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title={!canManage ? 'Permissão restrita para desativação' : (isPendente ? 'Remover convite' : 'Desativar')}
                  onClick={() => handlePermissionClick(canManage, () => requestDelete(p), 'Você não tem permissão para desativar professores.')}
                >
                  <PowerOff size={15} />
                </button>
              ) : (
                <button
                  className={`action-btn action-btn-success ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title={!canManage ? 'Permissão restrita para reativação' : 'Reativar'}
                  onClick={() => handlePermissionClick(canManage, () => handleReactivate(p), 'Você não tem permissão para reativar professores.')}
                >
                  <RotateCcw size={15} />
                </button>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="admin-page" style={{ marginTop: -24, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {!isLoading && (
        <SetupPageHero
          icon={UserCheck}
          etapa={5}
          titulo="Professores"
          descricao={
            materias.filter(m => m.ativo).length === 0
              ? 'Para cadastrar professores, primeiro cadastre as matérias da escola. Cada professor será vinculado às disciplinas que leciona.'
              : ativos === 0
                ? `Cadastre o corpo docente da escola. Cada professor será vinculado às matérias que leciona. ${materias.filter(m => m.ativo).length} matéria(s) disponíveis.`
                : 'Gerencie o corpo docente da escola. Edite vínculos de disciplinas, dados e status dos professores.'
          }
          bloqueado={materias.filter(m => m.ativo).length === 0}
          mensagemBloqueio="Cadastre Matérias antes dos Professores"
          acoes={[]}
          dismissivel={false}
          compact
        />
      )}
      <div className="ac-admin-data-card" style={{ background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <DataToolbar
          busca={searchQuery}
          onBuscaChange={setSearchQuery}
          placeholder="Buscar professor..."
          actions={
            <button
              type="button"
              className={`btn-primary ${!canManage ? 'opacity-50 cursor-not-allowed filter grayscale' : ''}`}
              onClick={() => handlePermissionClick(canManage, openNew, 'Você não tem permissão para cadastrar professores.')}
              title={!canManage ? 'Permissão restrita a Diretores e Administradores' : 'Cadastrar novo professor'}
              style={{ height: 34, padding: '0 12px', fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
            >
              <Plus size={15} /> Cadastrar Professor
            </button>
          }
          totalRecords={filtered.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[10, 20, 40, 100]}
          viewMode={viewMode}
          onViewModeChange={(m) => setViewMode(m as 'lista' | 'cards')}
          viewModes={['lista', 'cards']}
        />
        <div className="ac-admin-list-scroll" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', minHeight: 0 }}>
          {viewMode === 'lista' ? (
            <DataTable columns={columns} data={filtered} isLoading={isLoading} searchPlaceholder="Buscar professor..." showToolbar={false} pageSize={pageSize} />
          ) : (
            <div className="p-2 sm:p-3">
              {/* Mobile Compact Cards */}
              <div className="flex md:hidden flex-col gap-2">
                {filtered.map((p) => {
                  const totalMaterias = getMateriasVinculadas(p).length;
                  return (
                    <div
                      key={p.id}
                      className="border border-[var(--border-color,#e5e7eb)] rounded-xl p-2.5 bg-[var(--bg-card,white)] flex items-center gap-3 shadow-sm hover:shadow transition-shadow"
                    >
                      <div className="shrink-0">
                        <ListAvatar fotoUrl={(p as any).fotoUrl} nome={p.nome || 'Professor'} role="PROFESSOR" size={44} />
                      </div>

                      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                        <div className="flex items-center justify-between gap-1.5">
                          <span className="font-bold text-xs text-[var(--text-main,#1e293b)] truncate">
                            {p.nome}
                          </span>
                          <span className={`badge ${p.ativo ? 'badge-active' : 'badge-inactive'} shrink-0 text-[9px] px-1.5 py-0.5`}>
                            {p.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5 text-[10px] text-slate-500 flex-wrap">
                          <span className="font-semibold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                            {p.especialidade || 'Professor(a)'}
                          </span>
                          <span>•</span>
                          <span>{totalMaterias} matéria(s)</span>
                        </div>
                      </div>

                      <div className="shrink-0 flex items-center">
                        <RowActionMenu
                          items={[
                            {
                              label: 'Ver Detalhes',
                              icon: <Eye size={15} />,
                              onClick: () => setFichaId(p.id),
                              variant: 'view',
                            },
                            canManage
                              ? {
                                  label: 'Editar Professor',
                                  icon: <Pencil size={15} />,
                                  onClick: () => openEdit(p),
                                  variant: 'edit',
                                }
                              : null,
                            canManage && ((p as any).statusConta === 'PENDENTE' || (p as any).statusConta === 'ATIVANDO')
                              ? {
                                  label: 'Copiar Link de Ativação',
                                  icon: <Copy size={15} />,
                                  onClick: () => gerarNovoLink(p),
                                  variant: 'primary',
                                }
                              : null,
                            canManage && (p as any).statusConta === 'ATIVA'
                              ? {
                                  label: 'Suspender Professor',
                                  icon: <Ban size={15} />,
                                  onClick: () => requestChangeStatus(p, 'SUSPENSA'),
                                  variant: 'danger',
                                }
                              : null,
                            canManage && ((p as any).statusConta === 'SUSPENSA' || (p as any).statusConta === 'BLOQUEADA')
                              ? {
                                  label: 'Reativar / Desbloquear',
                                  icon: <PlayCircle size={15} />,
                                  onClick: () => requestChangeStatus(p, 'ATIVA'),
                                  variant: 'success',
                                }
                              : null,
                            canManage && p.ativo
                              ? {
                                  label: ((p as any).statusConta === 'PENDENTE' || (p as any).statusConta === 'ATIVANDO')
                                    ? 'Remover Convite'
                                    : 'Desativar Professor',
                                  icon: <PowerOff size={15} />,
                                  onClick: () => requestDelete(p),
                                  variant: 'danger',
                                }
                              : null,
                            canManage && !p.ativo
                              ? {
                                  label: 'Reativar Professor',
                                  icon: <RotateCcw size={15} />,
                                  onClick: () => handleReactivate(p),
                                  variant: 'success',
                                }
                              : null,
                          ]}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Full Cards */}
              <div className="hidden md:grid grid-cols-[repeat(auto-fill,minmax(200px,240px))] gap-4">
                {filtered.map((p) => {
                  const totalMaterias = getMateriasVinculadas(p).length;
                  return (
                    <div 
                      key={p.id} 
                      style={{ 
                        border: '1px solid var(--border-color, #e5e7eb)', 
                        borderRadius: 12, 
                        padding: '20px 16px', 
                        background: '#f8fafc',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        position: 'relative',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        gap: 12
                      }}
                    >
                      {/* Especialidade floating top-left */}
                      <span 
                        title="Especialidade" 
                        style={{ 
                          position: 'absolute', 
                          top: 12, 
                          left: 12, 
                          fontSize: '.72rem', 
                          fontWeight: 600, 
                          color: '#4b5563', 
                          background: '#e5e7eb', 
                          padding: '2px 8px', 
                          borderRadius: 6,
                          maxWidth: '90px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {p.especialidade || 'Professor(a)'}
                      </span>

                      {/* Status Badge floating top-right */}
                      <div style={{ position: 'absolute', top: 12, right: 12 }}>
                        <span className={`badge ${p.ativo ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: '.65rem', padding: '2px 8px' }}>
                          {p.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>

                      {/* Avatar on top */}
                      <div style={{ marginTop: 12 }}>
                        <ListAvatar fotoUrl={(p as any).fotoUrl} nome={p.nome || 'Professor'} role="PROFESSOR" size={120} />
                      </div>

                      {/* Data underneath */}
                      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.nome}
                        </div>
                        <div style={{ fontSize: '.76rem', color: '#2563eb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.email}
                        </div>
                        <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          Matérias: {totalMaterias}
                        </div>
                      </div>

                      {/* Actions at the bottom */}
                      <div className="w-full flex items-center justify-between border-t border-[var(--border-color,#e5e7eb)] pt-2.5 mt-auto">
                        <div className="flex md:hidden">
                          <RowActionMenu
                            items={[
                              {
                                label: 'Ver Detalhes',
                                icon: <Eye size={15} />,
                                onClick: () => setFichaId(p.id),
                                variant: 'view',
                              },
                              canManage
                                ? {
                                    label: 'Editar Professor',
                                    icon: <Pencil size={15} />,
                                    onClick: () => openEdit(p),
                                    variant: 'edit',
                                  }
                                : null,
                              canManage && p.ativo
                                ? {
                                    label: 'Desativar Professor',
                                    icon: <PowerOff size={15} />,
                                    onClick: () => requestDelete(p),
                                    variant: 'danger',
                                  }
                                : null,
                              canManage && !p.ativo
                                ? {
                                    label: 'Reativar Professor',
                                    icon: <RotateCcw size={15} />,
                                    onClick: () => handleReactivate(p),
                                    variant: 'success',
                                  }
                                : null,
                            ]}
                          />
                        </div>

                        <div className="hidden md:flex justify-center gap-1.5 w-full">
                          <button className="action-btn action-btn-view" title="Detalhes" onClick={() => setFichaId(p.id)}><Eye size={14} /></button>
                          {canManage && <button className="action-btn" title="Editar" onClick={() => openEdit(p)}><Pencil size={14} /></button>}
                          {canManage && (p.ativo ? (
                            <button className="action-btn action-btn-danger" title="Desativar" onClick={() => requestDelete(p)}><PowerOff size={14} /></button>
                          ) : (
                            <button className="action-btn action-btn-success" title="Reativar" onClick={() => handleReactivate(p)}><RotateCcw size={14} /></button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filtered.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Nenhum registro encontrado.</div>}
            </div>
          )}
          {viewMode === 'cards' && (
            <PaginationFooter
              totalItems={filtered.length}
              pageSize={pageSize}
            />
          )}
        </div>
      </div>
      <ProfessorForm 
        open={formOpen} 
        onClose={() => setFormOpen(false)} 
        editing={editing} 
        onCreated={(id) => { 
          setFormOpen(false); 
          refetch().then(() => setTimeout(() => setFichaId(id), 150)); 
          window.dispatchEvent(new CustomEvent('ac-data-refetch'));
        }} 
      />

      <Modal
        open={!!fichaId}
        onClose={() => setFichaId(null)}
        title="Ficha do professor"
        size="wide"
        contentClassName="ac-ficha-modal ac-professor-ficha-modal"
        hideCloseButton
        footer={
          fichaId ? (
            <ProfessorFichaFooter
              professor={professores.find((p) => p.id === fichaId) ?? { criadoEm: '', ativo: true }}
              canManage={canManage}
              onEdit={() => {
                const p = professores.find((x) => x.id === fichaId);
                setFichaId(null);
                if (p) openEdit(p);
              }}
              onDeactivate={() => {
                const p = professores.find((x) => x.id === fichaId);
                if (!p) return;
                setFichaId(null);
                if (p.ativo) requestDelete(p);
                else void handleReactivate(p);
              }}
            />
          ) : undefined
        }
      >
        {fichaId && escolaId ? (
          <ProfessorDetalhePage
            overrideId={fichaId}
            layout="desktop"
            onClose={() => setFichaId(null)}
          />
        ) : null}
      </Modal>

      <ConfirmModal
        open={!!deleteConfirm}
        title={
          deleteConfirm && ((deleteConfirm as any).statusConta === 'PENDENTE' || (deleteConfirm as any).statusConta === 'ATIVANDO')
            ? 'Remover convite'
            : 'Desativar professor'
        }
        message={
          deleteConfirm
            ? ((deleteConfirm as any).statusConta === 'PENDENTE' || (deleteConfirm as any).statusConta === 'ATIVANDO')
              ? `Remover o convite de "${deleteConfirm.nome}"? O registro será excluído permanentemente.`
              : `Desativar "${deleteConfirm.nome}"? O professor perderá acesso mas os dados serão preservados.`
            : ''
        }
        confirmLabel={
          deleteConfirm && ((deleteConfirm as any).statusConta === 'PENDENTE' || (deleteConfirm as any).statusConta === 'ATIVANDO')
            ? 'Remover'
            : 'Desativar'
        }
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDeleteProfessor()}
      />

      <ConfirmModal
        open={!!statusConfirm}
        title="Alterar status"
        message={
          statusConfirm
            ? `Deseja ${statusLabels[statusConfirm.novoStatus] || 'alterar'} "${statusConfirm.prof.nome}"?`
            : ''
        }
        confirmLabel="Confirmar"
        onCancel={() => setStatusConfirm(null)}
        onConfirm={() => void confirmChangeStatus()}
      />
    </div>
  );
}
